import { useEffect, useRef } from 'react'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'
import { useLearnProgressStore, type LearnProgress } from './learn-progress-store'

/**
 * Two-way sync of Learn curriculum progress with the user's server profile
 * (`learnProgress`), so completion + the active track follow the user across
 * devices. The SERVER is the source of truth; localStorage (in
 * learn-progress-store) is only the fast local cache. Mirrors
 * useThemeServerSync / useChartInteractionServerSync.
 *
 * - On first profile load: apply the server value via `hydrate()` (server wins
 *   on login). A user with no saved value triggers NO write: `lastSynced` seeds
 *   to local, so only a genuine change syncs up.
 * - On later local changes (complete a lesson, start a track): push the whole
 *   progress object up (deduped via JSON.stringify).
 *
 * Mount once inside the authenticated tree (AppLayout, next to the other sync
 * hooks).
 */
export function useLearnProgressServerSync(): void {
  const completed = useLearnProgressStore((s) => s.completed)
  const activeTrackId = useLearnProgressStore((s) => s.activeTrackId)
  const activeLessonIndex = useLearnProgressStore((s) => s.activeLessonIndex)
  const hydrate = useLearnProgressStore((s) => s.hydrate)
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const initialized = useRef(false)
  const lastSynced = useRef<string | null>(null)

  useEffect(() => {
    if (!user) return

    const local: LearnProgress = { completed, activeTrackId, activeLessonIndex }

    // First profile load: server wins. Return BEFORE the push path: `hydrate`
    // re-runs this effect with the server state, and this commit's slices are
    // still the stale local ones.
    if (!initialized.current) {
      initialized.current = true
      const server = validateServerValue(user.learnProgress)
      if (server) {
        lastSynced.current = JSON.stringify(server)
        hydrate(server)
      } else {
        // No saved value → don't write on login; seed to local.
        lastSynced.current = JSON.stringify(local)
      }
      return
    }

    // Later local changes: push the whole progress object up (deduped).
    const serialized = JSON.stringify(local)
    if (serialized !== lastSynced.current) {
      lastSynced.current = serialized
      void updateUser({ learnProgress: local })
    }
  }, [user, completed, activeTrackId, activeLessonIndex, hydrate, updateUser])
}

/** Loose validation of the server blob into a LearnProgress. Returns null when
 *  there's nothing meaningful saved (so login doesn't hydrate/write emptiness). */
function validateServerValue(
  raw: Record<string, unknown> | null | undefined,
): LearnProgress | null {
  if (!raw || typeof raw !== 'object') return null
  const completed = Array.isArray(raw.completed)
    ? raw.completed.filter((s): s is string => typeof s === 'string')
    : []
  const activeTrackId = typeof raw.activeTrackId === 'string' ? raw.activeTrackId : null
  const activeLessonIndex = typeof raw.activeLessonIndex === 'number' ? raw.activeLessonIndex : 0
  if (completed.length === 0 && activeTrackId === null) return null
  return { completed, activeTrackId, activeLessonIndex }
}
